<script setup lang="ts">
import { FileText, BookMarked, Link2, Check } from 'lucide-vue-next';
import SceneShell from '../SceneShell.vue';
</script>

<template>
    <SceneShell>
        <div
            class="pointer-events-none absolute top-1/2 left-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-400/10 blur-3xl"
        ></div>

        <!-- guide line -->
        <svg
            class="absolute inset-0 h-full w-full"
            viewBox="0 0 100 58"
            preserveAspectRatio="none"
            aria-hidden="true"
            fill="none"
        >
            <line
                x1="20"
                y1="29"
                x2="80"
                y2="29"
                stroke="rgba(56,189,248,0.3)"
                stroke-width="0.6"
                stroke-dasharray="2 2"
                class="dash"
            />
        </svg>

        <!-- PDF flying in from the left -->
        <div class="pdf" aria-hidden="true">
            <div
                class="card border-orange-400/50 bg-orange-400/10 text-orange-300"
            >
                <FileText class="h-5 w-5" />
            </div>
            <span class="tag text-orange-300/80">DOI</span>
        </div>

        <!-- Reference coming from the right -->
        <div class="ref" aria-hidden="true">
            <div class="card border-cyan-400/50 bg-cyan-400/10 text-cyan-200">
                <BookMarked class="h-5 w-5" />
            </div>
            <span class="tag text-cyan-200/80">DOI</span>
        </div>

        <!-- Link badge that snaps in the middle -->
        <div class="link-badge" aria-hidden="true">
            <span class="link-ring"></span>
            <div
                class="relative flex h-9 w-9 items-center justify-center rounded-full border border-emerald-400/60 bg-emerald-500 text-white"
            >
                <Link2 class="link-icon h-4 w-4" />
                <Check class="check-icon absolute h-4 w-4" />
            </div>
        </div>

        <p class="caption">
            Chaque PDF relié à sa référence, par les métadonnées.
        </p>
    </SceneShell>
</template>

<style scoped>
.card {
    display: flex;
    height: 3rem;
    width: 2.4rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    border-width: 1px;
}

.tag {
    margin-top: 0.3rem;
    display: block;
    text-align: center;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
}

.pdf,
.ref,
.link-badge {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
}

.pdf {
    left: 50%;
}
.ref {
    left: 50%;
}

.link-badge {
    left: 50%;
    z-index: 20;
    opacity: 0;
}

.link-ring {
    position: absolute;
    inset: -4px;
    border-radius: 9999px;
    border: 1px solid rgba(16, 185, 129, 0.5);
}

.check-icon {
    opacity: 0;
}

.caption {
    position: absolute;
    bottom: 0.75rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 11px;
    color: rgb(100 116 139);
}

/* Static (reduced motion) resting position: docked together near centre */
.pdf {
    left: 42%;
}
.ref {
    left: 58%;
}
.link-badge {
    opacity: 1;
}
.check-icon {
    opacity: 1;
}
.link-icon {
    opacity: 0;
}

@media (prefers-reduced-motion: no-preference) {
    .dash {
        animation: dash-flow 1s linear infinite;
    }
    .pdf {
        animation: pdf-move 4.4s ease-in-out infinite;
    }
    .ref {
        animation: ref-move 4.4s ease-in-out infinite;
    }
    .link-badge {
        animation: link-pop 4.4s ease-in-out infinite;
    }
    .link-ring {
        animation: link-ring 4.4s ease-in-out infinite;
    }
    .link-icon {
        animation: link-icon 4.4s steps(1) infinite;
    }
    .check-icon {
        animation: check-icon 4.4s steps(1) infinite;
    }

    @keyframes dash-flow {
        to {
            stroke-dashoffset: -4;
        }
    }

    @keyframes pdf-move {
        0% {
            left: 12%;
            opacity: 0;
        }
        12% {
            opacity: 1;
        }
        45%,
        72% {
            left: 42%;
            opacity: 1;
        }
        88%,
        100% {
            left: 42%;
            opacity: 0;
        }
    }

    @keyframes ref-move {
        0% {
            left: 88%;
            opacity: 0;
        }
        12% {
            opacity: 1;
        }
        45%,
        72% {
            left: 58%;
            opacity: 1;
        }
        88%,
        100% {
            left: 58%;
            opacity: 0;
        }
    }

    @keyframes link-pop {
        0%,
        44% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.6);
        }
        52%,
        72% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
        }
        84%,
        100% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.6);
        }
    }

    @keyframes link-ring {
        0%,
        46% {
            opacity: 0;
            transform: scale(0.6);
        }
        54% {
            opacity: 1;
            transform: scale(1);
        }
        72% {
            opacity: 0;
            transform: scale(1.6);
        }
        100% {
            opacity: 0;
        }
    }

    @keyframes link-icon {
        0%,
        53% {
            opacity: 1;
        }
        54%,
        100% {
            opacity: 0;
        }
    }

    @keyframes check-icon {
        0%,
        53% {
            opacity: 0;
        }
        54%,
        100% {
            opacity: 1;
        }
    }
}
</style>
